import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Sparkles, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KpiPill, MarkdownView } from './shared';
import { toast } from 'sonner';
import { DateField } from '@/components/common/DateField';

const API = process.env.REACT_APP_BACKEND_URL;

export function InformesSection({ authHeader }) {
  const qc = useQuery;
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [form, setForm] = useState({ desde: firstOfMonth.toISOString().slice(0, 10), hasta: today.toISOString().slice(0, 10), sede: '', provincia: '' });
  const [generating, setGenerating] = useState(false);
  const [latest, setLatest] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const { data: list } = useQuery({
    queryKey: ['acc-informes-list'],
    queryFn: () => axios.get(`${API}/api/accidentes/informes/list`, { headers: authHeader() }).then(r => r.data),
  });

  const generate = async () => {
    setGenerating(true); setLatest(null);
    try {
      const res = await axios.post(`${API}/api/accidentes/informe-ia`, form, { headers: authHeader(), timeout: 90000 });
      setLatest(res.data);
      const { toast } = await import('sonner');
      toast.success('Informe generado con Claude Sonnet 4.6');
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e?.response?.data?.detail || 'Error generando informe');
    } finally { setGenerating(false); }
  };

  const loadInforme = async (id) => {
    const res = await axios.get(`${API}/api/accidentes/informes/${id}`, { headers: authHeader() });
    setLatest(res.data);
  };

  const downloadPdf = async (id) => {
    if (!id) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/accidentes/informes/${id}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 30000,
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      // Try to get filename from content-disposition header
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `informe_accidentes.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado correctamente');
    } catch {
      toast.error('Error al generar el PDF');
    } finally { setDownloading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="accidentes-informes">
      <div className="lg:col-span-1 space-y-4">
        <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
          <h4 className="text-xs font-semibold flex items-center gap-1.5"><Sparkles size={12} /> Generar informe IA</h4>
          <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Claude Sonnet 4.6 analizará los accidentes del período junto con OPS/Liderazgo/NMs en los mismos centros y producirá un informe ejecutivo para Dirección.</p>
          <div><Label className="text-[10px]">Desde</Label><DateField value={form.desde} onChange={v => setForm({...form,desde:v})} className="w-full" testId="acc-informe-desde" /></div>
          <div><Label className="text-[10px]">Hasta</Label><DateField value={form.hasta} onChange={v => setForm({...form,hasta:v})} className="w-full" testId="acc-informe-hasta" /></div>
          <div><Label className="text-[10px]">Centro (opcional)</Label><Input value={form.sede} onChange={e => setForm({...form,sede:e.target.value})} placeholder="Código sede ej. 5000045" className="h-8 text-xs" /></div>
          <div><Label className="text-[10px]">Provincia (opcional)</Label><Input value={form.provincia} onChange={e => setForm({...form,provincia:e.target.value})} placeholder="ej. Madrid" className="h-8 text-xs" /></div>
          <Button size="sm" className="w-full h-8 text-xs gap-1.5" onClick={generate} disabled={generating} data-testid="acc-informe-generate">
            <Sparkles size={11} /> {generating ? 'Generando (puede tardar 30s)...' : 'Generar informe'}
          </Button>
        </div>
        <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
          <h4 className="text-xs font-semibold">Histórico</h4>
          {list?.items?.length ? list.items.map(i => (
            <button key={i.id} onClick={() => loadInforme(i.id)} className="w-full text-left p-2 rounded hover:bg-white/5 text-[11px]" style={{ color: 'var(--vz-text-secondary)' }} data-testid={`acc-informe-item-${i.id}`}>
              <div>{i.desde} → {i.hasta}</div>
              <div className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{i.total_accidentes} acc · {new Date(i.generated_at).toLocaleDateString('es-ES')}</div>
            </button>
          )) : <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>Sin informes previos.</p>}
        </div>
      </div>
      <div className="lg:col-span-2">
        {latest ? (
          <div className="rounded-xl p-6" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
            <div className="flex justify-between items-start mb-4 pb-4 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <div>
                <h3 className="font-bold text-white">Informe {latest.desde} → {latest.hasta}</h3>
                <p className="text-[11px] mt-1" style={{ color: 'var(--vz-text-muted)' }}>Generado por {latest.generated_by} · Modelo: {latest.model}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="flex gap-2">
                  <KpiPill label="Acc" value={latest.total_accidentes} />
                  <KpiPill label="OPS" value={latest.ops_count} />
                  <KpiPill label="Charlas" value={latest.lid_count} />
                  <KpiPill label="NM" value={latest.nm_count} />
                </div>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs gap-1.5"
                  onClick={() => downloadPdf(latest.id)} disabled={downloading}
                  data-testid="acc-informe-pdf-btn"
                >
                  <Download size={11} /> {downloading ? 'Generando...' : 'PDF'}
                </Button>
              </div>
            </div>
            <div className="prose prose-invert prose-sm max-w-none text-xs" style={{ color: 'var(--vz-text-primary)' }}>
              <MarkdownView md={latest.informe_md} />
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
            <Sparkles size={40} className="mx-auto mb-3" style={{ color: 'var(--vz-text-muted)' }} />
            <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Configura el período y pulsa "Generar informe" para que la IA produzca el análisis ejecutivo.</p>
          </div>
        )}
      </div>
    </div>
  );
}
